import * as duration from 'dayjs/plugin/duration';

import { UserExperienceService } from '@services/user-experience.service';
import {
  isString,
  SDuration,
  ServiceLocator
} from '@summit/core';
import {
  DayOfWeek,
  DayOfWeekAbbrMapping,
  DayOfWeekMapping,
  NumberFormatter
} from '@summit/models';

import { ScheduleStatus } from '../schedule/schedule-status.enum';
import { ScheduleLegBase } from '../shared/schedule-leg-base';
import { RouteAltWeek } from './route-alt-week';
import { RouteRun } from './route-run';

export class ScheduleTemplateLeg extends ScheduleLegBase {
  altWeekId: string;
  altWeek: RouteAltWeek;
  runDayOfWeek: DayOfWeek;
  departureDayOfWeek: DayOfWeek;
  departureTime: duration.Duration;
  arrivalDayOfWeek: DayOfWeek;
  arrivalTime: duration.Duration;
  assignDriver1: boolean;
  assignDriver2: boolean;
  assignTruckAlias: boolean;
  //For sorting Purposes
  private endOfScheduleWeek: DayOfWeek = DayOfWeek.Sunday;

  public get runNum(): number {
    return this.run?.num ?? 1;
  }
  public set runNum(value: number) {
    /*NOP*/
  }

  public get defaultSort(): string {
    if (
      this.runDayOfWeek - this.departureDayOfWeek >= 2 ||
      (this.runDayOfWeek == 0 && this.departureDayOfWeek >= 5) ||
      (this.runDayOfWeek == 1 && this.departureDayOfWeek == 6)
    ) {
      return `${this.altWeek?.name}:${this.runDayOfWeek}:${NumberFormatter(this.run.num, 2)}:${NumberFormatter(
        this.legCount,
        2
      )}:${NumberFormatter(this.departureDayOfWeek + 7, 2)}`;
    }
    return `${this.altWeek?.name}:${this.runDayOfWeek}:${NumberFormatter(this.run?.num, 2)}:${NumberFormatter(
      this.legCount,
      2
    )}:${NumberFormatter(this.departureDayOfWeek, 2)}`;
  }

  constructor(jsonData?: Partial<ScheduleTemplateLeg>, endOfWeekOverride?: DayOfWeek) {
    super(jsonData);
    //console.log('building off of json', jsonData);
    //* Configure week correctly
    if (endOfWeekOverride || endOfWeekOverride == 0) {
      this.endOfScheduleWeek = endOfWeekOverride;
    } else {
      const uxService = ServiceLocator.injector?.get(UserExperienceService);
      if (uxService) {
        const currentUX = uxService.getUserExperience();
        this.endOfScheduleWeek = currentUX?.endOfScheduleWeek ?? this.endOfScheduleWeek;
        //console.log(`Using EndOfScheduleWeek: ${DayOfWeekMapping[this.endOfScheduleWeek]} from`, currentUX);
      } else {
        //console.log(`Using Default EndOfScheduleWeek: ${DayOfWeekMapping[this.endOfScheduleWeek]}`);
      }
    }

    if (jsonData) {
      if (isString(this.legCount)) this.legCount = parseInt(this.legCount);
      if (isString(this.runDayOfWeek)) this.runDayOfWeek = parseInt(this.runDayOfWeek);
      if (isString(this.departureDayOfWeek)) this.departureDayOfWeek = parseInt(this.departureDayOfWeek);
      if (isString(this.arrivalDayOfWeek)) this.arrivalDayOfWeek = parseInt(this.arrivalDayOfWeek);
      if (isNaN(this.arrivalDayOfWeek)) this.arrivalDayOfWeek = null;
      if (isString(this.origHubID)) this.origHubID = parseInt(this.origHubID);
      if (isString(this.destHubID)) this.destHubID = parseInt(this.destHubID);
      if (jsonData.altWeek) this.altWeek = new RouteAltWeek(jsonData.altWeek);
      if (jsonData.run) this.run = new RouteRun(jsonData.run);
      if (jsonData.departureTime) this.departureTime = SDuration.new(jsonData.departureTime);
      if (jsonData.arrivalTime) this.arrivalTime = SDuration.new(jsonData.arrivalTime);
    }
    this.departureTime ??= null;
    this.arrivalTime ??= null;
    this.assignDriver1 ??= true;
    this.assignDriver2 ??= true;
    this.assignTruckAlias ??= true;
    this.status ??= ScheduleStatus.Active;
    //console.log('built', this);
  }

  get scheduleDayOrder(): string {
    const response = this.runDayOfWeek <= this.endOfScheduleWeek ? this.runDayOfWeek + 7 : this.runDayOfWeek;
    //console.log(`Returning ${response} for ${this.runDayOfWeek} in week ending ${this.endOfScheduleWeek}`)
    return NumberFormatter(response, 2);
  }

  get departureDateTimeString(): string {
    return this.departureTime
      ? `${DayOfWeekAbbrMapping[this.departureDayOfWeek]} ${SDuration.ToAmPM(this.departureTime)}`
      : `${DayOfWeekMapping[this.departureDayOfWeek]}`;
  }

  get arrivalDateTimeString(): string | null {
    return this.arrivalTime
      ? `${DayOfWeekAbbrMapping[this.arrivalDayOfWeek]} ${SDuration.ToAmPM(this.arrivalTime)}`
      : this.arrivalDayOfWeek
      ? `${DayOfWeekMapping[this.arrivalDayOfWeek]}`
      : null;
  }

  override toShortString(): string {
    let response = `${DayOfWeekMapping[this.runDayOfWeek]?.substring(
      0,
      2
    )} ${this.run?.toShortString()} ${this.origHub?.toShortString()} => ${this.destHub?.toShortString()}`;
    if (this.truckAlias) response += ` - ${this.truckAlias.name}`;
    if (this.driver1) response += ` - ${this.driver1.name}`;
    if (this.driver2) response += ` - ${this.driver2.name}`;
    return response;
  }
}
